import { pdfjs } from 'react-pdf';
import { PDFDocument } from 'pdf-lib';
import { Editor, createShapeId, AssetRecordType, Box, TLAssetId, TLShapeId } from 'tldraw';
import api from '@/api/api';

interface PdfPage {
  src: string;
  bounds: Box;
  assetId: TLAssetId;
  shapeId: TLShapeId;
}

interface Pdf {
  name: string;
  pages: PdfPage[];
  source: string;
}

export const usePdfManager = (
  meetingUid?: string,
  pdfContainerRef?: React.RefObject<HTMLDivElement>
) => {
  const pageSpacing = 32;

  const loadPdf = async (name: string, source: string): Promise<Pdf | undefined> => {
    if (!pdfContainerRef?.current || !source) {
      return;
    }

    const pdf = await pdfjs.getDocument(source).promise;
    const pages: PdfPage[] = [];

    const canvas = window.document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Failed to create canvas context');

    const visualScale = 1.5;
    const scale = window.devicePixelRatio;

    let top = 0;
    let widest = 0;
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: scale * visualScale });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const renderContext = {
        canvasContext: context,
        viewport
      };
      await page.render(renderContext).promise;

      const width = viewport.width / scale;
      const height = viewport.height / scale;

      const pageKey = `${name}-page-${i}`;

      pages.push({
        src: canvas.toDataURL(),
        bounds: new Box(0, top, width, height),
        assetId: AssetRecordType.createId(pageKey),
        shapeId: createShapeId(pageKey)
      });
      top += height + pageSpacing;
      widest = Math.max(widest, width);
    }
    canvas.width = 0;
    canvas.height = 0;

    for (const page of pages) {
      page.bounds.x = (widest - page.bounds.width) / 2;
    }

    return {
      name,
      pages,
      source
    };
  };

  const exportPdf = async (editor: Editor, { name, source, pages }: Pdf) => {
    if (!meetingUid) {
      return;
    }

    const response = await fetch(source);
    const pdfData = await response.arrayBuffer();

    const pdf = await PDFDocument.load(pdfData);

    const pdfPages = pdf.getPages();
    if (pdfPages.length !== pages.length) {
      throw new Error('PDF page count mismatch');
    }

    const allShapes = editor.getCurrentPageShapes();
    const pageShapeIds = new Set(pages.map((page) => page.shapeId));
    const allIds = Array.from(editor.getCurrentPageShapeIds()).filter(
      (id) => !pageShapeIds.has(id)
    );
    if (allShapes.length === pageShapeIds.size) return;

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i];
      const pdfPage = pdfPages[i];

      const bounds = page.bounds;
      const shapesInBounds = allIds.filter((id) => {
        const shapePageBounds = editor.getShapePageBounds(id);
        if (!shapePageBounds) return false;
        return shapePageBounds.collides(bounds);
      });

      if (shapesInBounds.length === 0) {
        continue;
      }

      const exportedPng = await editor.toImage(shapesInBounds, {
        format: 'png',
        background: false,
        bounds: page.bounds,
        padding: 0,
        scale: 1
      });

      pdfPage.drawImage(await pdf.embedPng(await exportedPng.blob.arrayBuffer()), {
        x: 0,
        y: 0,
        width: pdfPage.getWidth(),
        height: pdfPage.getHeight()
      });
    }

    const pdfBytes = await pdf.save();
    const blob = new File([pdfBytes as unknown as BlobPart], name, { type: 'application/pdf' });

    await api.uploadMeetingDrawingPdf(meetingUid, blob);
  };

  return { loadPdf, exportPdf };
};
